<script setup lang="ts">
import type { SiteBlockData, SiteBlockSettings } from '~/types/types'
import { usePublicSiteRepository } from '~/repositories/publicSiteRepository'
import { formatMinor } from '~/utils/money'

/**
 * The tenant's published rate card.
 *
 * Reads the live catalogue rather than storing prices in the block, so a
 * price changed in Agent → Price list is correct on the public page the
 * moment it is saved. A block that carried its own copy would silently
 * quote last month's tariff.
 *
 * Two shapes, because a 33-row translation tariff and a 4-service menu want
 * different things: `table` is a dense priced list grouped by category,
 * `cards` is a short highlight set.
 */
const props = defineProps<{ data: SiteBlockData, settings: SiteBlockSettings }>()

const repo = usePublicSiteRepository()
const { t } = useI18n()

const category = computed(() => (props.data.category as string) || undefined)
const limit = computed(() => (props.data.limit as number) || undefined)
const layout = computed(() => (props.data.layout === 'cards' ? 'cards' : 'table'))

const { data: response, error } = await useAsyncData(
  () => `price-list-${category.value ?? 'all'}-${limit.value ?? 'all'}`,
  () => repo.getPriceList({ category: category.value, limit: limit.value }),
  { watch: [category, limit] }
)

const payload = computed(() => response.value?.data ?? null)
const groups = computed(() => payload.value?.groups ?? [])
const currency = computed(() => payload.value?.currency ?? 'MNT')
const isEmpty = computed(() => groups.value.every(g => !g.items.length))

const money = (minor: number | null | undefined) => formatMinor(minor, currency.value)

/** "15,000₮", "20,000₮–30,000₮", or "from 25,000₮" for a per-unit price. */
/**
 * A catalogue row with no price is priced on request — interpreting, or
 * anything the agency quotes case by case. Rendering money(null) left a
 * blank cell that read as a bug, so such rows say so explicitly and can
 * still be ordered: they are quote-only, so nothing is charged up front.
 */
function price(item: { price_amount: number | null, price_max: number | null, unit: string, is_band: boolean }): string {
  if (item.price_amount === null || item.price_amount === undefined) {
    return t('site.services_on_request')
  }
  return item.is_band && item.price_max
    ? `${money(item.price_amount)}–${money(item.price_max)}`
    : money(item.price_amount)
}

/** "per page" / "per copy" — the unit is part of the price, not a footnote. */
function unitLabel(unit: string): string {
  return unit && unit !== 'item' ? t(`site.services_unit_${unit}`) : ''
}

/**
 * Ordering is OPT-IN per block. An agency that only wants to publish a
 * tariff should not silently acquire a checkout, and one that has no
 * payment method configured would send customers into a dead end.
 */
const ordering = computed(() => props.data.enable_ordering === true)
const cart = useCart()
const localePath = useLocalePath()
onMounted(() => cart.hydrate())

function addToCart(item: { id: number, name: string, unit: string }) {
  cart.add(item as never)
}

/**
 * Search + collapsible categories.
 *
 * A 35-row tariff where every row shouts equally is unscannable on a phone:
 * someone who wants one birth certificate has to read all of it. Search is
 * the tool for people who know what they need; collapsing turns the rest
 * into a nine-item menu.
 *
 * A collapsed category still shows its count and price RANGE, so folding
 * the list never hides the pricing story — which matters on a page whose
 * whole promise is transparent pricing.
 */
const query = ref('')
const fold = (v: string) => v.toLocaleLowerCase()

const totalItems = computed(() =>
  groups.value.reduce((n, g) => n + g.items.length, 0))

const matchedGroups = computed(() => {
  const q = fold(query.value.trim())
  if (!q) return groups.value
  return groups.value
    .map(g => ({ ...g, items: g.items.filter(i => fold(i.name).includes(q) || fold(i.note ?? '').includes(q)) }))
    .filter(g => g.items.length > 0)
})

const matchCount = computed(() =>
  matchedGroups.value.reduce((n, g) => n + g.items.length, 0))

const groupKey = (g: { category: string | null }, i: number) => g.category ?? `__g${i}`

/**
 * Short lists open; long ones start folded. A four-service menu hiding
 * itself behind clicks would be silly, and a 35-row tariff opening to a
 * wall of text is the problem being solved.
 */
const startFolded = computed(() => totalItems.value > 15 && groups.value.length > 2)

const openKeys = ref<string[]>([])
watch([groups, startFolded], () => {
  openKeys.value = startFolded.value ? [] : groups.value.map(groupKey)
}, { immediate: true })

// Searching overrides the fold: a hit the customer cannot see is useless.
const isOpen = (key: string) => query.value.trim() !== '' || openKeys.value.includes(key)

function toggle(key: string) {
  openKeys.value = openKeys.value.includes(key)
    ? openKeys.value.filter(k => k !== key)
    : [...openKeys.value, key]
}

const allOpen = computed(() => groups.value.every((g, i) => openKeys.value.includes(groupKey(g, i))))
function toggleAll() {
  openKeys.value = allOpen.value ? [] : groups.value.map(groupKey)
}

/** "15,000₮" or "15,000₮–45,000₮" for a folded category. */
function rangeLabel(items: Array<{ price_amount: number | null, price_max: number | null }>): string {
  const lows = items.map(i => i.price_amount).filter((v): v is number => v !== null && v !== undefined)
  if (!lows.length) return t('site.services_on_request')
  const highs = items.map(i => i.price_max ?? i.price_amount).filter((v): v is number => v !== null && v !== undefined)
  const min = Math.min(...lows)
  const max = Math.max(...highs)
  return min === max ? money(min) : `${money(min)}–${money(max)}`
}

const showNotes = computed(() => props.data.show_notes !== false)
const showSurcharges = computed(() => props.data.show_surcharges === true)

/**
 * Surcharge groups worth showing. One whose options are ALL free is hidden:
 * telling a visitor "Urgent: +0%" is noise, and the agency has simply not
 * priced it yet.
 */
const surcharges = computed(() =>
  (payload.value?.modifiers ?? [])
    .map(g => ({ ...g, options: g.options.filter(o => !o.is_free) }))
    .filter(g => g.options.length > 0)
)

function surchargeLabel(o: { kind: string, rate: number, amount: number }): string {
  return o.kind === 'percent' ? `+${o.rate}%` : `+${money(o.amount)}`
}

const isExternal = (url?: string | null) => !!url && /^(https?:|mailto:|tel:)/.test(url)
</script>

<template>
  <div>
    <SiteSectionHeading
      :eyebrow="data.eyebrow"
      :title="data.title"
      :subtitle="data.subtitle"
      class="mb-8"
    />

    <!-- The page must not look broken when the catalogue is empty or the
         request failed; say which it is. -->
    <p
      v-if="error"
      class="text-sm text-[var(--site-muted)]"
    >
      {{ t('site.services_error') }}
    </p>

    <p
      v-else-if="isEmpty"
      class="text-sm text-[var(--site-muted)]"
    >
      {{ t('site.services_empty') }}
    </p>

    <!-- ── Table: dense, grouped, price-led ─────────────────────────── -->
    <div
      v-else-if="layout === 'table'"
      :class="ordering && cart.count.value > 0 ? 'pb-24' : ''"
    >
      <!-- Search first: the fastest path for someone who already knows which
           document they need. Only offered once the list is long enough to
           be worth searching. -->
      <div
        v-if="totalItems > 8"
        class="mb-6 flex flex-wrap items-center gap-3"
      >
        <!-- type=text rather than search: WebKit adds its own clear button
             to a search input, which sat beside ours — two × controls. Ours
             works in every browser, so it is the one that stays. -->
        <div class="relative min-w-0 flex-1">
          <input
            v-model="query"
            type="text"
            :placeholder="t('site.services_search')"
            :aria-label="t('site.services_search')"
            class="w-full rounded border border-[var(--site-border)] bg-transparent px-3 py-2.5 pr-9 text-sm"
          >
          <button
            v-if="query"
            type="button"
            class="absolute inset-y-0 right-0 px-3 text-[var(--site-muted)]"
            :aria-label="t('site.services_search_clear')"
            @click="query = ''"
          >
            ×
          </button>
        </div>
        <button
          v-if="!query"
          type="button"
          class="site-btn-outline min-h-10 shrink-0 px-4 py-2.5 text-sm font-medium transition"
          @click="toggleAll"
        >
          {{ allOpen ? t('site.services_collapse_all') : t('site.services_expand_all') }}
        </button>
      </div>

      <!-- Searching for something that isn't here must say so, not show a
           blank page. -->
      <p
        v-if="query && matchCount === 0"
        class="py-6 text-sm text-[var(--site-muted)]"
      >
        {{ t('site.services_no_match', { q: query }) }}
      </p>

      <p
        v-else-if="query"
        class="mb-4 text-sm text-[var(--site-muted)]"
      >
        {{ t('site.services_match_count', { n: matchCount }) }}
      </p>

      <section
        v-for="(group, gi) in matchedGroups"
        :key="groupKey(group, gi)"
        class="border-t border-[var(--site-border)] first:border-t-0"
      >
        <!-- The header is the toggle. A folded category still reports how
             many services it holds and what they cost, so folding never
             hides the price story. -->
        <button
          v-if="group.category"
          type="button"
          class="-mx-3 flex w-[calc(100%+1.5rem)] items-center gap-3 rounded px-3 py-4 text-left transition hover:bg-[var(--site-accent-soft)]"
          :aria-expanded="isOpen(groupKey(group, gi))"
          @click="toggle(groupKey(group, gi))"
        >
          <span
            class="shrink-0 text-xs transition-transform"
            :class="isOpen(groupKey(group, gi)) ? 'rotate-90' : ''"
            aria-hidden="true"
          >▶</span>
          <span class="site-display min-w-0 flex-1 text-lg tracking-tight">{{ group.category }}</span>
          <span class="shrink-0 text-right text-xs text-[var(--site-muted)]">
            <span class="hidden sm:inline">{{ t('site.services_count', { n: group.items.length }) }} · </span>{{ rangeLabel(group.items) }}
          </span>
        </button>

        <ul
          v-show="!group.category || isOpen(groupKey(group, gi))"
          class="divide-y divide-[var(--site-border)] pb-4"
        >
          <li
            v-for="item in group.items"
            :key="item.id"
            class="-mx-3 flex flex-col gap-2 rounded px-3 py-3 transition hover:bg-[var(--site-accent-soft)] sm:flex-row sm:items-center sm:justify-between sm:gap-4"
          >
            <!-- Explicit two-row stack on phones rather than flex-wrap. Wrapping
                 let each row break differently depending on how long the
                 Mongolian name happened to be: some rows kept the price on
                 line one, others dropped it to line two LEFT-aligned with dead
                 space beside it, and row heights came out 61/93/117px. The
                 price and the button now sit on their own line, pinned to
                 opposite edges, so every row reads the same. -->
            <div class="min-w-0">
              <p class="font-medium">
                {{ item.name }}
              </p>
              <!-- Note below the name, not inline after it: inline, a long
                   note pushed the name's line box and competed with it. -->
              <p
                v-if="showNotes && item.note"
                class="text-sm text-[var(--site-muted)]"
              >
                {{ item.note }}
              </p>
            </div>

            <div class="flex shrink-0 items-center justify-between gap-3 sm:justify-end">
              <div class="sm:text-right">
                <span class="font-semibold tabular-nums">{{ price(item) }}</span>
                <span
                  v-if="unitLabel(item.unit)"
                  class="ml-1 text-sm text-[var(--site-muted)]"
                >{{ unitLabel(item.unit) }}</span>
              </div>
              <button
                v-if="ordering"
                type="button"
                class="min-h-10 shrink-0 px-4 py-2.5 text-sm font-semibold transition"
                :class="cart.has(item.id) ? 'site-btn-outline' : 'site-btn-accent'"
                :aria-label="`${t('site.order_add')}: ${item.name}`"
                @click="addToCart(item)"
              >
                {{ cart.has(item.id) ? t('site.order_added') : t('site.order_add') }}
              </button>
            </div>
          </li>
        </ul>
      </section>
    </div>

    <!-- ── Cards: a short highlight set ─────────────────────────────── -->
    <div
      v-else
      class="grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
    >
      <template
        v-for="(group, gi) in groups"
        :key="group.category ?? `g${gi}`"
      >
        <div
          v-for="item in group.items"
          :key="item.id"
          class="site-surface-card flex flex-col p-6"
        >
          <span
            v-if="group.category"
            class="site-bg-accent-soft site-text-accent mb-3 self-start rounded-full px-2.5 py-1 text-xs font-semibold"
          >{{ group.category }}</span>

          <h4 class="site-display text-lg tracking-tight">
            {{ item.name }}
          </h4>

          <p class="mt-2 flex items-baseline gap-1">
            <span class="site-display text-2xl tracking-tight">{{ price(item) }}</span>
            <span
              v-if="unitLabel(item.unit)"
              class="text-sm text-[var(--site-muted)]"
            >{{ unitLabel(item.unit) }}</span>
          </p>

          <p
            v-if="showNotes && item.note"
            class="mt-2 text-sm text-[var(--site-muted)]"
          >
            {{ item.note }}
          </p>
        </div>
      </template>
    </div>

    <!-- ── Surcharges ───────────────────────────────────────────────── -->
    <div
      v-if="showSurcharges && surcharges.length"
      class="mt-8 border-t border-[var(--site-border)] pt-5"
    >
      <p class="mb-2 text-sm font-semibold">
        {{ t('site.services_surcharges') }}
      </p>
      <ul class="flex flex-wrap gap-x-5 gap-y-1 text-sm text-[var(--site-muted)]">
        <li
          v-for="g in surcharges"
          :key="g.group"
        >
          <span
            v-for="(o, i) in g.options"
            :key="o.key"
          >
            <span v-if="i > 0"> · </span>{{ o.label }} {{ surchargeLabel(o) }}
          </span>
        </li>
      </ul>
    </div>

    <p
      v-if="!isEmpty && !error"
      class="mt-6 text-xs text-[var(--site-muted)]"
    >
      {{ t('site.services_disclaimer') }}
    </p>

    <!-- Sticky, because a cart the customer cannot see is a cart they forget
         they started. Deliberately NOT teleported to body: the --site-*
         variables are scoped to [data-site-theme] on .site-scope inside
         SiteShell, so a teleported bar lands outside that subtree and every
         themed colour resolves to nothing — the button rendered as bare
         text. In-flow `sticky bottom-0` keeps it inside the theme and still
         pins it to the viewport. Padded for the iOS home indicator. -->
    <div
      v-if="ordering && cart.count.value > 0"
      class="sticky bottom-0 z-40 -mx-6 mt-8 border-t border-[var(--site-border)] bg-[var(--site-surface)] px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-6"
    >
      <div class="mx-auto flex max-w-5xl items-center justify-between gap-3">
        <span class="min-w-0 truncate text-sm font-medium">
          {{ t('site.order_cart_bar', { n: cart.count.value }) }}
        </span>
        <NuxtLink
          :to="localePath('/order')"
          class="site-btn-accent shrink-0 px-5 py-2.5 text-sm font-semibold transition"
        >
          {{ t('site.order_cart_continue') }} →
        </NuxtLink>
      </div>
    </div>

    <div
      v-if="data.cta_label && data.cta_url"
      class="mt-6"
    >
      <a
        v-if="isExternal(data.cta_url)"
        :href="data.cta_url"
        class="site-btn-accent inline-block px-6 py-3 font-semibold transition"
        rel="noopener"
      >{{ data.cta_label }}</a>
      <NuxtLink
        v-else
        :to="data.cta_url"
        class="site-btn-accent inline-block px-6 py-3 font-semibold transition"
      >{{ data.cta_label }}</NuxtLink>
    </div>
  </div>
</template>
